import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, Plus, Trash2, ChevronDown, ChevronRight, Save, ArrowLeft,
  Upload, FileText, Video, GripVertical, X, Eye,
} from "lucide-react";
import { getToken, formatApiError } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

const STATUS_OPTIONS = [
  { value: "available", label: "Disponibile" },
  { value: "coming_soon", label: "In arrivo" },
  { value: "archived", label: "Archiviato" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

export default function CoursesTab() {
  const [view, setView] = useState({ mode: "list", slug: null }); // list | edit | new
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/courses`, { headers: authHdr() });
      setList(data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (view.mode === "list") reload(); }, [view.mode]);

  if (view.mode === "edit" || view.mode === "new") {
    return (
      <CourseEditor
        slug={view.slug}
        isNew={view.mode === "new"}
        onBack={() => setView({ mode: "list" })}
      />
    );
  }

  return (
    <div data-testid="admin-courses-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-xs uppercase tracking-[0.18em] text-[#3A4F6B]">
          {list?.total ?? 0} corsi
        </p>
        <button
          onClick={() => setView({ mode: "new" })}
          data-testid="admin-new-course-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#1E3557]"
        >
          <Plus size={13} /> Nuovo corso
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={18} /><span className="text-sm">Caricamento...</span></div>
      ) : error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : !list?.items?.length ? (
        <div className="border border-dashed border-[#E5E1DA] py-16 text-center text-sm text-[#3A4F6B]">Nessun corso ancora. Crea il primo.</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
          {list.items.map((c) => (
            <article key={c.id} data-testid={`admin-course-${c.slug}`} className="bg-[#FFFFFF] p-6 flex flex-col">
              <div className="flex items-baseline justify-between gap-2">
                <p className="overline text-[10px]">{c.slug}</p>
                <StatusBadge status={c.status} />
              </div>
              <h3 className="font-serif-display text-xl mt-3 leading-tight text-[#0B192C]">{c.title}</h3>
              <p className="mt-2 text-xs font-light text-[#3A4F6B] flex-1">{c.subtitle}</p>
              <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
                <span>{c.modules_count} moduli</span>
                <span>·</span>
                <span>{c.lessons_count} lezioni</span>
              </div>
              <p className="mt-1 text-[10px] text-[#3A4F6B]">Modificato {fmtDate(c.updated_at)}</p>
              <button
                onClick={() => setView({ mode: "edit", slug: c.slug })}
                data-testid={`edit-course-${c.slug}`}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6]"
              >
                Modifica
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    available: { label: "Disponibile", cls: "bg-[#0E7C8F] text-[#F9F8F6]" },
    coming_soon: { label: "In arrivo", cls: "bg-[#F4E8D6] text-[#0B192C]" },
    archived: { label: "Archiviato", cls: "bg-[#E5E1DA] text-[#3A4F6B]" },
  };
  const s = map[status] || { label: status, cls: "bg-[#E5E1DA] text-[#0B192C]" };
  return <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${s.cls}`}>{s.label}</span>;
}

// ============================================================
// COURSE EDITOR
// ============================================================

function CourseEditor({ slug, isNew, onBack }) {
  const [course, setCourse] = useState(
    isNew ? {
      slug: "", title: "", subtitle: "", status: "available",
      description: "", cover_image: "", modules: [],
    } : null
  );
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openModule, setOpenModule] = useState(0);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/courses/${slug}`, { headers: authHdr() });
        setCourse(data);
      } catch (err) { setError(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [slug, isNew]);

  const setField = (k, v) => setCourse((c) => ({ ...c, [k]: v }));

  const addModule = () => {
    setCourse((c) => ({
      ...c,
      modules: [...(c.modules || []), { n: (c.modules?.length || 0) + 1, title: "Nuovo modulo", intro: "", lessons: [] }],
    }));
    setOpenModule((c) => (course?.modules?.length || 0));
  };

  const removeModule = (idx) => {
    if (!window.confirm("Eliminare il modulo e tutte le lezioni?")) return;
    setCourse((c) => ({ ...c, modules: c.modules.filter((_, i) => i !== idx).map((m, i) => ({ ...m, n: i + 1 })) }));
  };

  const updateModule = (idx, patch) => {
    setCourse((c) => ({ ...c, modules: c.modules.map((m, i) => i === idx ? { ...m, ...patch } : m) }));
  };

  const addLesson = (modIdx) => {
    updateModule(modIdx, {
      lessons: [...(course.modules[modIdx].lessons || []), { title: "Nuova lezione", content: "", video_url: "", pdf_url: "" }],
    });
  };

  const removeLesson = (modIdx, lessonIdx) => {
    if (!window.confirm("Eliminare la lezione?")) return;
    updateModule(modIdx, {
      lessons: course.modules[modIdx].lessons.filter((_, i) => i !== lessonIdx),
    });
  };

  const updateLesson = (modIdx, lessonIdx, patch) => {
    updateModule(modIdx, {
      lessons: course.modules[modIdx].lessons.map((l, i) => i === lessonIdx ? { ...l, ...patch } : l),
    });
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      if (isNew) {
        if (!course.slug.trim()) { setError("Slug obbligatorio"); setSaving(false); return; }
        await axios.post(`${API}/admin/courses`, course, { headers: authHdr() });
      } else {
        await axios.put(`${API}/admin/courses/${course.slug}`, {
          title: course.title, subtitle: course.subtitle, status: course.status,
          description: course.description, cover_image: course.cover_image,
          modules: course.modules,
        }, { headers: authHdr() });
      }
      onBack();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE il corso "${course.title}"? Operazione irreversibile.`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${course.slug}`, { headers: authHdr() });
      onBack();
    } catch (err) { setError(formatApiError(err)); }
  };

  if (loading) return <div className="flex items-center gap-3 text-[#3A4F6B]"><Loader2 className="animate-spin" size={18} /><span className="text-sm">Caricamento...</span></div>;
  if (!course) return <p className="text-sm text-red-700">{error || "Corso non trovato"}</p>;

  return (
    <div data-testid="course-editor" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3 sticky top-[52px] z-[5] bg-[#F9F8F6] py-3 -my-3 border-b border-[#E5E1DA]">
        <button
          onClick={onBack}
          data-testid="course-editor-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#E89B6C]"
        >
          <ArrowLeft size={14} /> Indietro
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={remove}
              data-testid="course-delete-btn"
              className="inline-flex items-center gap-2 px-4 py-2 border border-red-700 text-red-700 text-xs uppercase tracking-[0.18em] hover:bg-red-700 hover:text-white"
            >
              <Trash2 size={13} /> Elimina corso
            </button>
          )}
          <button
            onClick={save}
            disabled={saving}
            data-testid="course-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.18em] hover:bg-[#1E3557] disabled:opacity-60"
          >
            {saving ? <><Loader2 size={13} className="animate-spin" /> Salvataggio...</> : <><Save size={13} /> Salva</>}
          </button>
        </div>
      </div>

      {error && <p data-testid="course-editor-error" className="text-sm text-red-700">{error}</p>}

      {/* Course meta */}
      <div className="bg-white border border-[#E5E1DA] p-6 space-y-4">
        <h3 className="font-serif-display text-xl text-[#0B192C]">Informazioni corso</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <FieldLabel label="Slug (URL univoco)">
            <input type="text" value={course.slug} disabled={!isNew}
              onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              data-testid="course-slug-input"
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-mono focus:outline-none focus:border-[#0B192C] disabled:bg-[#F4E8D6]/30" />
          </FieldLabel>
          <FieldLabel label="Stato">
            <select value={course.status} onChange={(e) => setField("status", e.target.value)}
              data-testid="course-status-input"
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]">
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FieldLabel>
        </div>
        <FieldLabel label="Titolo">
          <input type="text" value={course.title} onChange={(e) => setField("title", e.target.value)}
            data-testid="course-title-input"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
        </FieldLabel>
        <FieldLabel label="Sottotitolo">
          <input type="text" value={course.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)}
            data-testid="course-subtitle-input"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
        </FieldLabel>
        <FieldLabel label="Descrizione (markdown ammesso)">
          <textarea rows={3} value={course.description || ""} onChange={(e) => setField("description", e.target.value)}
            data-testid="course-description-input"
            className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
        </FieldLabel>
      </div>

      {/* Modules */}
      <div className="bg-white border border-[#E5E1DA] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-[#0B192C]">Moduli ({course.modules?.length || 0})</h3>
          <button onClick={addModule} data-testid="add-module-btn" className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#0B192C] text-xs uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6]">
            <Plus size={12} /> Aggiungi modulo
          </button>
        </div>

        {(course.modules || []).map((m, midx) => (
          <ModuleEditor
            key={midx}
            module={m}
            isOpen={openModule === midx}
            onToggle={() => setOpenModule(openModule === midx ? -1 : midx)}
            onUpdate={(patch) => updateModule(midx, patch)}
            onRemove={() => removeModule(midx)}
            onAddLesson={() => addLesson(midx)}
            onUpdateLesson={(li, patch) => updateLesson(midx, li, patch)}
            onRemoveLesson={(li) => removeLesson(midx, li)}
            midx={midx}
          />
        ))}
      </div>
    </div>
  );
}

function ModuleEditor({ module: m, isOpen, onToggle, onUpdate, onRemove, onAddLesson, onUpdateLesson, onRemoveLesson, midx }) {
  return (
    <div className="border border-[#E5E1DA]" data-testid={`module-${midx}`}>
      <div className="flex items-center gap-2 bg-[#F4E8D6]/40 px-3 py-2 cursor-pointer" onClick={onToggle}>
        <GripVertical size={14} className="text-[#3A4F6B]" />
        <span className="overline text-[#0B192C]">Modulo {m.n}</span>
        <span className="text-sm font-light flex-1 truncate">{m.title}</span>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">{m.lessons?.length || 0} lezioni</span>
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-red-700 hover:text-red-900 ml-2"
          data-testid={`remove-module-${midx}`}
        >
          <Trash2 size={13} />
        </button>
      </div>
      {isOpen && (
        <div className="p-4 space-y-3 bg-white">
          <FieldLabel label="Titolo modulo">
            <input type="text" value={m.title} onChange={(e) => onUpdate({ title: e.target.value })}
              data-testid={`module-title-${midx}`}
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
          </FieldLabel>
          <FieldLabel label="Intro (breve)">
            <textarea rows={2} value={m.intro || ""} onChange={(e) => onUpdate({ intro: e.target.value })}
              data-testid={`module-intro-${midx}`}
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
          </FieldLabel>

          <div className="border-t border-[#E5E1DA] pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="overline">Lezioni ({m.lessons?.length || 0})</p>
              <button onClick={onAddLesson} data-testid={`add-lesson-${midx}`} className="inline-flex items-center gap-1 px-2 py-1 border border-[#0B192C] text-[10px] uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6]">
                <Plus size={10} /> Lezione
              </button>
            </div>
            {(m.lessons || []).map((l, li) => (
              <LessonEditor
                key={li}
                lesson={l}
                onUpdate={(patch) => onUpdateLesson(li, patch)}
                onRemove={() => onRemoveLesson(li)}
                midx={midx}
                li={li}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LessonEditor({ lesson, onUpdate, onRemove, midx, li }) {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const uploadFile = async (file, field) => {
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHdr(), "Content-Type": "multipart/form-data" },
      });
      onUpdate({ [field]: `${BACKEND}${data.url}` });
    } catch (err) { setUploadErr(formatApiError(err)); }
    finally { setUploading(false); }
  };

  return (
    <div className="border border-[#E5E1DA] mb-2" data-testid={`lesson-${midx}-${li}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-[#F9F8F6] cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <FileText size={12} className="text-[#3A4F6B]" />
        <span className="text-sm flex-1 truncate">{lesson.title || "(senza titolo)"}</span>
        {lesson.video_url && <Video size={11} className="text-[#0E7C8F]" />}
        {lesson.pdf_url && <FileText size={11} className="text-[#E89B6C]" />}
        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-red-700 hover:text-red-900" data-testid={`remove-lesson-${midx}-${li}`}>
          <Trash2 size={11} />
        </button>
      </div>
      {expanded && (
        <div className="p-3 space-y-3 bg-white">
          <FieldLabel label="Titolo lezione">
            <input type="text" value={lesson.title} onChange={(e) => onUpdate({ title: e.target.value })}
              data-testid={`lesson-title-${midx}-${li}`}
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm focus:outline-none focus:border-[#0B192C]" />
          </FieldLabel>
          <FieldLabel label="Contenuto testo (markdown supportato)">
            <textarea rows={6} value={lesson.content || ""} onChange={(e) => onUpdate({ content: e.target.value })}
              data-testid={`lesson-content-${midx}-${li}`}
              placeholder="Markdown: **grassetto**, *corsivo*, # heading, - lista&#10;Doppio newline = nuovo paragrafo"
              className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-mono focus:outline-none focus:border-[#0B192C]" />
          </FieldLabel>
          <div className="grid md:grid-cols-2 gap-3">
            <FieldLabel label="Video URL (YouTube/Vimeo embed)">
              <input type="url" value={lesson.video_url || ""} onChange={(e) => onUpdate({ video_url: e.target.value })}
                placeholder="https://www.youtube.com/embed/VIDEO_ID"
                data-testid={`lesson-video-${midx}-${li}`}
                className="w-full px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-mono focus:outline-none focus:border-[#0B192C]" />
            </FieldLabel>
            <FieldLabel label="PDF allegato (upload o URL)">
              <div className="flex gap-2">
                <input type="text" value={lesson.pdf_url || ""} onChange={(e) => onUpdate({ pdf_url: e.target.value })}
                  placeholder="URL PDF o carica..."
                  data-testid={`lesson-pdf-url-${midx}-${li}`}
                  className="flex-1 px-3 py-2 border border-[#0B192C]/20 bg-white text-sm font-mono focus:outline-none focus:border-[#0B192C]" />
                <label className="inline-flex items-center gap-1 px-3 py-2 border border-[#0B192C] text-[10px] uppercase tracking-[0.18em] hover:bg-[#0B192C] hover:text-[#F9F8F6] cursor-pointer">
                  {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                  Carica
                  <input type="file" accept=".pdf,image/*,audio/*" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "pdf_url")}
                    data-testid={`lesson-pdf-upload-${midx}-${li}`} />
                </label>
              </div>
              {uploadErr && <p className="text-[10px] text-red-700 mt-1">{uploadErr}</p>}
            </FieldLabel>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
